import { defineStore } from 'pinia'

// Channels hidden from the channel list on this device only («Скрыть канал» in the mobile channel
// Drawer) — a local preference, not synced: it lives in localStorage, per group. «Показать скрытые
// каналы» in a category's or the group's «...» menu brings them back.
const STORAGE_KEY = 'hiddenChannelsByGroup'

function load(): Record<string, string[]> {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

export const useHiddenChannelsStore = defineStore('hiddenChannels', {
  state: () => ({
    byGroup: load() as Record<string, string[]>,
  }),

  getters: {
    isHidden: (state) => (groupId: string, channelId: string) =>
      state.byGroup[groupId.toLowerCase()]?.includes(channelId.toLowerCase()) ?? false,
  },

  actions: {
    persist() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.byGroup))
      } catch {
        // storage unavailable — the choice just doesn't survive a restart
      }
    },

    hide(groupId: string, channelId: string) {
      const key = groupId.toLowerCase()
      const id = channelId.toLowerCase()
      const list = this.byGroup[key] ?? []
      if (list.includes(id)) return
      this.byGroup = { ...this.byGroup, [key]: [...list, id] }
      this.persist()
    },

    /** Brings back the given channels (a category's), or every hidden channel of the group. */
    show(groupId: string, channelIds?: string[]) {
      const key = groupId.toLowerCase()
      const list = this.byGroup[key]
      if (!list) return
      const wanted = channelIds ? new Set(channelIds.map((id) => id.toLowerCase())) : null
      const rest = wanted ? list.filter((id) => !wanted.has(id)) : []
      const next = { ...this.byGroup }
      if (rest.length) next[key] = rest
      else delete next[key]
      this.byGroup = next
      this.persist()
    },

    resetForLogout() {
      this.byGroup = {}
      try {
        localStorage.removeItem(STORAGE_KEY)
      } catch {
        // storage unavailable - nothing persisted to remove
      }
    },
  },
})
